<template>
	<div>
		<div class="d-flex align-items-center mb-2">
			<strong class="mr-2">Extraire / éclater une colonne JSON</strong>
			<b-button size="sm" variant="outline-secondary" v-b-modal="'modal-split-json-tuto-' + id" title="Comment ça marche ?">
				<i class="fas fa-question-circle"></i>
			</b-button>
		</div>

		<b-form-group label="Colonne à extraire" label-cols="3">
			<b-form-input v-model="column" :list="'split-json-cols-' + id" placeholder="Nom de la colonne..." />
			<datalist :id="'split-json-cols-' + id">
				<option v-for="col in availableColumnsList" :key="col" :value="col">{{ col }}</option>
			</datalist>
		</b-form-group>

		<b-form-group label="Nombre de passes" label-cols="3"
			description="Applique l'extraction N fois en séquence (utile pour des JSON imbriqués)">
			<b-form-input type="number" v-model.number="time" min="1" style="width:100px" />
		</b-form-group>

		<b-form-checkbox v-model="only_first" class="mb-2">
			Récupérer seulement le premier élément (tableaux)
		</b-form-checkbox>

		<b-form-checkbox v-model="delete_column" class="mb-2">
			Supprimer la colonne de départ après extraction
		</b-form-checkbox>

		<b-form-checkbox v-model="keep_empty">
			Conserver les lignes avec tableau vide ou valeur absente
			<small class="text-muted d-block">Sans cette option, une ligne dont la colonne contient <code>[]</code> ou <code>null</code> est supprimée du résultat.</small>
		</b-form-checkbox>

		<!-- Modale tutoriel -->
		<b-modal :id="'modal-split-json-tuto-' + id" title="Comment fonctionne l'extraction JSON ?" size="xl" ok-only ok-title="Fermer" scrollable>
			<div class="split-tuto">

				<p>Cette étape lit le contenu JSON d'une colonne et l'éclate selon son type.</p>

				<section class="split-tuto-section">
					<h5>Cas 1 — La colonne contient un tableau <code>["a", "b", "c"]</code></h5>
					<p>Chaque élément devient une ligne séparée. La colonne source est remplacée par la valeur de l'élément.</p>
					<div class="split-tuto-table">
						<div class="split-tuto-col">
							<strong>Avant</strong>
							<table class="table table-sm table-bordered mt-1">
								<thead><tr><th>id</th><th>tags</th></tr></thead>
								<tbody>
									<tr><td>1</td><td>["sport","musique","voyage"]</td></tr>
								</tbody>
							</table>
						</div>
						<div class="split-tuto-arrow">→</div>
						<div class="split-tuto-col">
							<strong>Après</strong>
							<table class="table table-sm table-bordered mt-1">
								<thead><tr><th>id</th><th>tags</th></tr></thead>
								<tbody>
									<tr><td>1</td><td>sport</td></tr>
									<tr><td>1</td><td>musique</td></tr>
									<tr><td>1</td><td>voyage</td></tr>
								</tbody>
							</table>
						</div>
					</div>
					<p class="mt-2">
						<b-badge variant="info">Option "Premier élément seulement"</b-badge>
						Avec cette option, seul <code>"sport"</code> est conservé — la ligne n'est pas dupliquée.
					</p>
				</section>

				<section class="split-tuto-section">
					<h5>Cas 2 — La colonne contient un objet <code>{"ville": "Paris", "cp": "75001"}</code></h5>
					<p>Les clés de l'objet deviennent de nouvelles colonnes sur la même ligne. La colonne source est supprimée si "Supprimer la colonne de départ" est coché.</p>
					<div class="split-tuto-table">
						<div class="split-tuto-col">
							<strong>Avant</strong>
							<table class="table table-sm table-bordered mt-1">
								<thead><tr><th>id</th><th>adresse</th></tr></thead>
								<tbody>
									<tr><td>1</td><td>{"ville":"Paris","cp":"75001"}</td></tr>
								</tbody>
							</table>
						</div>
						<div class="split-tuto-arrow">→</div>
						<div class="split-tuto-col">
							<strong>Après</strong>
							<table class="table table-sm table-bordered mt-1">
								<thead><tr><th>id</th><th>adresse</th><th>ville</th><th>cp</th></tr></thead>
								<tbody>
									<tr><td>1</td><td>{"ville":"Paris"...}</td><td>Paris</td><td>75001</td></tr>
								</tbody>
							</table>
						</div>
					</div>
					<p class="mt-2 text-muted"><small>Si une valeur extraite est elle-même un tableau ou un objet, elle est re-sérialisée en JSON (String) dans la nouvelle colonne.</small></p>
				</section>

				<section class="split-tuto-section">
					<h5>Cas 3 — La colonne contient un tableau d'objets <code>[{"nom":"Alice"},{"nom":"Bob"}]</code></h5>
					<p>Chaque objet devient une ligne. La colonne source reçoit la valeur <strong>sérialisée en JSON</strong> de chaque objet (pas les clés directement).</p>
					<div class="split-tuto-table">
						<div class="split-tuto-col">
							<strong>Avant</strong>
							<table class="table table-sm table-bordered mt-1">
								<thead><tr><th>id</th><th>personnes</th></tr></thead>
								<tbody>
									<tr><td>1</td><td>[{"nom":"Alice"},{"nom":"Bob"}]</td></tr>
								</tbody>
							</table>
						</div>
						<div class="split-tuto-arrow">→</div>
						<div class="split-tuto-col">
							<strong>Après (1 passe)</strong>
							<table class="table table-sm table-bordered mt-1">
								<thead><tr><th>id</th><th>personnes</th></tr></thead>
								<tbody>
									<tr><td>1</td><td>{"nom":"Alice"}</td></tr>
									<tr><td>1</td><td>{"nom":"Bob"}</td></tr>
								</tbody>
							</table>
						</div>
					</div>
					<p class="mt-2">
						Pour aller plus loin et obtenir <code>nom</code> comme colonne séparée, configurez <strong>Nombre de passes = 2</strong> ou ajoutez une deuxième étape d'extraction sur la même colonne.
					</p>
					<div class="split-tuto-table">
						<div class="split-tuto-col">
							<strong>Après (2 passes)</strong>
							<table class="table table-sm table-bordered mt-1">
								<thead><tr><th>id</th><th>personnes</th><th>nom</th></tr></thead>
								<tbody>
									<tr><td>1</td><td>{"nom":"Alice"}</td><td>Alice</td></tr>
									<tr><td>1</td><td>{"nom":"Bob"}</td><td>Bob</td></tr>
								</tbody>
							</table>
						</div>
					</div>
				</section>

				<section class="split-tuto-section">
					<h5>Cas 4 — La valeur n'est pas du JSON valide</h5>
					<p>La ligne est conservée inchangée. Aucune erreur bloquante.</p>
				</section>

				<section class="split-tuto-section">
					<h5>Option "Nombre de passes"</h5>
					<p>Applique l'extraction N fois en séquence, comme si vous aviez N étapes identiques à la suite. Utile pour des JSON imbriqués. Exemple : tableau → objet en 2 passes.</p>
				</section>

			</div>
		</b-modal>
	</div>
</template>

<script>
	import uniqueId from "lodash.uniqueid";

	export default {
		name: "split_json_step",
		props: {
			available_columns: {
				default: () => []
			},
			data: {
				default: () => []
			}
		},
		data() {
			return {
				id: uniqueId(""),
				column: "",
				time: 1,
				only_first: false,
				delete_column: false,
                keep_empty: false
			};
		},
		computed: {
			availableColumnsList() {
				var cols = this.available_columns || [];
				if (!cols.length && this.data && this.data.length > 0)
					cols = Object.keys(this.data[0]);
				return cols;
			}
		},
		methods: {
			save() {
				return {
					column: this.column,
					time: this.time || 1,
					only_first: this.only_first,
					delete_column: this.delete_column,
                    keep_empty: this.keep_empty
				};
			},
			load(data) {
				if (!data) return;
				this.column = data.column || "";
				this.time = data.time || 1;
				this.only_first = !!data.only_first;
				this.delete_column = !!data.delete_column;
                this.keep_empty = data.keep_empty || false;
			}
		}
	};
</script>

<style scoped>
.split-tuto {
	font-size: 0.9em;
	line-height: 1.6;
}
.split-tuto-section {
	margin-bottom: 1.5rem;
	padding-bottom: 1.5rem;
	border-bottom: 1px solid #dee2e6;
}
.split-tuto-section:last-child {
	border-bottom: none;
}
.split-tuto-section h5 {
	font-weight: 600;
	margin-bottom: 0.6rem;
	color: #343a40;
}
.split-tuto-table {
	display: flex;
	align-items: flex-start;
	gap: 12px;
	flex-wrap: wrap;
}
.split-tuto-col {
	flex: 1;
	min-width: 200px;
}
.split-tuto-arrow {
	font-size: 1.5em;
	padding-top: 28px;
	color: #6c757d;
}
</style>
